import React, {
  createContext,
  useContext,
  useState,
  useEffect,
  useRef,
  useCallback,
} from "react";
import { useSession } from "../authentications/SessionContext";
import { toast, Bounce } from "react-toastify";
import { searchReadGeneral } from "../DataApi/generalApis/searchReadGeneral";
import { useNotifications } from "./NotificationProvider";

const PollingContext = createContext();

export const PollingProvider = ({ children }) => {
  const [importedFileId, setImportedFileId] = useState(
    () => localStorage.getItem("importedFileId") || null
  );
  const [isPolling, setIsPolling] = useState(
    () => JSON.parse(localStorage.getItem("isPolling")) || false
  );
  const [progress, setProgress] = useState(
    () => JSON.parse(localStorage.getItem("progress")) || 0
  );
  const { sessionInfo } = useSession();
  const { company_id, context } = sessionInfo;
  const { notifications, addNotification, updateNotification } =
    useNotifications();
  const intervalRef = useRef(null);
  const jobQueuePagination = { limit: 100, offset: 0 };
  const jobQueueFields = [
    "name",
    "model_name",
    "state",
    "date_created",
    "eta",
    "date_done",
    "exec_time",
    "exc_name",
    "exc_message",
    "uuid",
    "channel",
    "company_id",
    "result",
  ];

  // Start polling function
  const startPolling = useCallback((fileId) => {
    setImportedFileId(fileId);
    setIsPolling(true);

    localStorage.setItem("importedFileId", fileId);
    localStorage.setItem("isPolling", true);

    toast("Product bulk upload: 0%", {
      toastId: "bulk-upload-toast",
      position: "bottom-right",
      autoClose: false,
      closeOnClick: false,
      draggable: false,
      closeButton: false,
      progress: 0,
      type: "success",
      theme: "dark",
      transition: Bounce,
    });
  }, []);

  // Stop polling function
  const stopPolling = useCallback(() => {
    setIsPolling(false);
    clearInterval(intervalRef.current);
    intervalRef.current = null;

    localStorage.removeItem("importedFileId");
    localStorage.removeItem("isPolling");
    localStorage.removeItem("progress");

    const toastTimeout = setTimeout(() => {
      if (toast.isActive("bulk-upload-toast")) {
        toast.dismiss("bulk-upload-toast");
      }
    }, 3000);

    return () => clearTimeout(toastTimeout); // Cleanup timeout
  }, []);

  // Polling effect
  useEffect(() => {
    if (!importedFileId || !isPolling) return;

    const pollApi = async () => {
      try {
        const jobQueueResponse = await searchReadGeneral({
          company_id,
          domain: [
            ["imported_file_id", "=", importedFileId],
            ["job_function_id", "=", 3],
          ],
          context,
          fields: jobQueueFields,
          pagination: jobQueuePagination,
          model: "queue.job",
        });

        if (jobQueueResponse?.result?.records.length > 0) {
          const records = jobQueueResponse.result.records;
          const totalRecords = records.length;
          const completed = records.filter((record) =>
            ["done", "failed"].includes(record.state)
          ).length;
          const progressValue =
            totalRecords === 0 ? 0 : completed / totalRecords;

          setProgress(progressValue);
          localStorage.setItem("progress", JSON.stringify(progressValue));

          if (toast.isActive("bulk-upload-toast")) {
            toast.update("bulk-upload-toast", {
              render: `Product bulk upload: ${(progressValue * 100).toFixed(
                0
              )}%`,
              progress: progressValue,
            });
          }

          records.forEach((record) => {
            const { id, name, state, date_created, result } = record;
            const existingNotification = notifications.find((n) => n.id === id);

            const colorMap = {
              done: "success",
              failed: "error",
              pending: "warning",
              enqueued: "info",
            };

            if (!existingNotification) {
              addNotification({
                id,
                name,
                state,
                date: date_created,
                result,
                color: colorMap[state],
              });
            } else if (existingNotification.state !== state) {
              updateNotification({
                id,
                state,
                result,
                color: colorMap[state],
              });
            }
          });

          const allProcessed = records.every((record) =>
            ["done", "failed"].includes(record.state)
          );

          if (allProcessed) {
            stopPolling();
          }
        }
        // else {
        //   stopPolling();
        // }
      } catch (error) {
        console.error("Polling Error:", error);
      }
    };

    // Start polling
    if (!intervalRef.current) {
      intervalRef.current = setInterval(pollApi, 5000);
    }

    return () => {
      clearInterval(intervalRef.current);
      intervalRef.current = null; // Ensure interval cleanup
    };
  }, [
    importedFileId,
    isPolling,
    company_id,
    context,
    notifications,
    addNotification,
    updateNotification,
    stopPolling,
  ]);

  // Restore polling state on mount
  useEffect(() => {
    if (isPolling && importedFileId) {
      const savedProgress = JSON.parse(localStorage.getItem("progress")) || 0;
      toast("Product bulk upload: 0%", {
        toastId: "bulk-upload-toast",
        position: "bottom-right",
        autoClose: false,
        closeOnClick: false,
        draggable: false,
        closeButton: false,
        progress: savedProgress,
        type: "success",
        theme: "dark",
        transition: Bounce,
      });
    }
  }, [isPolling, importedFileId]);

  // Cleanup on unmount
  useEffect(() => {
    return () => {
      clearInterval(intervalRef.current);
      intervalRef.current = null;
    };
  }, []);

  return (
    <PollingContext.Provider value={{ startPolling, stopPolling, isPolling }}>
      {children}
    </PollingContext.Provider>
  );
};

export const usePolling = () => {
  return useContext(PollingContext);
};
